import clsx from "clsx";
import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isToday,
  startOfMonth,
  subDays,
  getISODay,
} from "date-fns";
import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { vi, enUS } from "date-fns/locale";
import { toast } from "react-toastify";
import HolidayIcon16pxSvg from "../../../assets/images/icons/HolidayIcon16pxSvg";
import LeaveIcon16pxSvg from "../../../assets/images/icons/LeaveIcon16pxSvg";
import QuestionIcon16pxSvg from "../../../assets/images/icons/QuestionIcon16pxSvg";
import RecurringHolidaysIcon16pxSvg from "../../../assets/images/icons/RecurringHolidaysIcon16pxSvg";
import LeaveApi from "../../../axios/LeaveAndHoliday/LeaveApi";
import DefaultTooltip from "../../../components/DefaultTooltip";
import { useLoading } from "../../../context/LoadingContext";
import { useSettings } from "../../../context/SettingsContext";
import LeaveHolidayIcon20pxSvg from "./../../../assets/images/icons/LeaveHolidayIcon20pxSvg";
import MoreActionClickedTooltip from "./MoreActionClickedTooltip";
import MoreActionTooltip from "./MoreActionTooltip";
import SmsIcon from "@mui/icons-material/Sms";
import { useAuth } from "../../../context/AuthContext";
import { DateTime } from "luxon";
import { ControlpanelSuccess3thButton } from "../../../components/StyledButton";
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import IconButton from "@mui/material/IconButton";
import UserRemoveIcon16pxSvg from "../../../assets/images/icons/UserRemoveIcon16pxSvg";
import ShiftRosterIcon20pxSvg from "../../../assets/images/icons/ShiftRosterIcon20pxSvg";

// const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getNextMonthDays = (lastDayOfMonth, emptyCell) => {
  const nextMonthDays = [];
  for (let i = 1; i <= emptyCell; i++) {
    nextMonthDays.push(addDays(lastDayOfMonth, i));
  }
  return nextMonthDays;
};

const getPrevMonthDays = (firstDayOfMonth, emptyCell) => {
  const prevMonthDays = [];
  for (let i = 1; i <= emptyCell; i++) {
    prevMonthDays.unshift(subDays(firstDayOfMonth, i));
  }
  return prevMonthDays;
};

const MonthEventsCalendar = (props) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("vi") ? vi : enUS;

  const [legends, setLegends] = useState(() => [
    {
      color: <div className="rounded-full w-3 h-3 bg-[#0057FF]" />,
      label: t("common.current-day"),
    },
    {
      color: <div className="rounded-full w-3 h-3 bg-[#BF83FF]" />,
      label: t("common.leaves"),
    },
    {
      color: <div className="rounded-full w-3 h-3 bg-[#3CD856]" />,
      label: t("common.holidays"),
    },
      {
        color: <div className="rounded-full w-3 h-3 bg-[#FA5A7D]" />,
        label: t("common.recurring-holidays"),
      },
    ]);

  useEffect(() => {
    setLegends([
      {
        color: <div className="rounded-full w-3 h-3 bg-[#0057FF]" />,
        label: t("common.current-day"),
      },
      {
        color: <div className="rounded-full w-3 h-3 bg-[#FF0066]" />,
        label: t("common.leaves"),
      },
      {
        color: <div className="rounded-full w-3 h-3 bg-[#853EEF]" />,
        label: t("common.holidays"),
      },
      {
        color: <div className="rounded-full w-3 h-3 bg-[#058900]" />,
        label: t("common.recurring-holidays"),
      },
    ]);
    // quan trọng: dep theo language
  }, [i18n.language, t]);


  const months = [
    t("common.january"),
    t("common.february"),
    t("common.march"),
    t("common.april"),
    t("common.may"),
    t("common.june"),
    t("common.july"),
    t("common.august"),
    t("common.september"),
    t("common.october"),
    t("common.november"),
    t("common.december"),
  ];
  const weekDays = [
    t("common.monday3"),
    t("common.tuesday3"),
    t("common.wednesday3"),
    t("common.thursday3"),
    t("common.friday3"),
    t("common.saturday3"),
    t("common.sunday3"),
  ];
  const [show, hide] = useLoading();
  const { isAdmin, profile, genaralSettingTimeZone } = useAuth();
  const { refreshData, groupId } = props;
  const { calendarLeave } = LeaveApi();
  const { settings } = useSettings();
  const [monthCalendarItemData, setMonthCalendarItemData] = useState([]);
  const { setSelectedDate } = props;
  const [currentDate, setCurrentDate] = useState(new Date());
  const currentMonthByText = months[currentDate.getMonth()];
  const currentYear = currentDate.getFullYear();
  const firstDayOfMonth = startOfMonth(currentDate);
  const lastDayOfMonth = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({
    start: firstDayOfMonth,
    end: lastDayOfMonth,
  });
  const navigate = useNavigate();

  const handlePrevMonth = () => {
    setCurrentDate(prev =>
      new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(prev =>
      new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );
  };

  useEffect(() => {
    props.calculateDisplayedWeeks(currentDate, 1); // 1 = Monday
  }, [currentDate]);
  // const startingDateIndex = getDay(firstDayOfMonth);
  const startingDateIndex = getISODay(firstDayOfMonth) - 1;
  const totalCells = 35;
  const emptyCellsForNextMonth =
    totalCells - (daysInMonth.length + startingDateIndex);
  const nextMonthDays = getNextMonthDays(
    lastDayOfMonth,
    emptyCellsForNextMonth
  );
  const prevMonthDays = getPrevMonthDays(firstDayOfMonth, startingDateIndex);

  // Luôn khóa ngày đang chọn = hôm nay, không cho chọn ngày khác
  useEffect(() => {
    setSelectedDate(new Date());
  }, [setSelectedDate]);

  const getEventsForDay = (day) => {
    const formattedDay = format(day, "yyyy-MM-dd");
    return monthCalendarItemData.filter((event) => event.date === formattedDay);
  };

  // const getTooltipTitle = (day) => {
  //    return format(day, `${settings.dateFormat} (iiii)`);
  // };

  const getTooltipTitle = (day) => {
    return format(day, `${settings.dateFormat} (EEEE)`, { locale });
  };

  const getMonthRangeUTC = (date) => {
    const start = new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      1,
      0, 0, 0
    ));

    const end = new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth() + 1,
      0,
      23, 59, 59
    ));

    return {
      from: start.toISOString(),
      to: end.toISOString(),
    };
  };

  const getMonthRangeByTZ = (date, timeZone) => {
    const base = DateTime.fromJSDate(date, {
      zone: timeZone || "UTC",
    });

    return {
      from: base.startOf("month").toUTC().toISO(),
      to: base.endOf("month").toUTC().toISO(),
    };
  };

  useEffect(() => {
    const fetchCalendarEventList = async () => {
      try {
        const { from, to } = getMonthRangeByTZ(currentDate, genaralSettingTimeZone);
        show();

        const payload = {
          from,
          to,
          langId: localStorage.getItem("i18nextLngId"),
          userId: isAdmin ? null : profile.userId,
          groupId: props.groupId
        };
        const response = await calendarLeave(payload);
        if (
          response.data.message === "Successful" ||
          response.data.message === "Thành công"
        ) {
          setMonthCalendarItemData(response.data.data);
        }
      } catch (error) {
        if (error.response) {
          if (error.response.status !== 401) {
            toast.error(t(error?.response.data.message));
          } else {
            toast.error(t('common.network-timeout'));
          }
        }
      } finally {
        hide();
      }
    };

    fetchCalendarEventList();
  }, [refreshData, groupId, currentDate]);

  return (
    <div className="card px-3 py-3">
      <div className="flex gap-2 items-center">
        <p className="text-[18px] max-3xl:text-lg font-semibold">
          {t("common.leaves-and-holidays")}
        </p>
      </div>
      <div className="month-calendar mt-4">
        <div className="relative h-10 flex items-center">
          <IconButton
            size="small"
            onClick={handlePrevMonth}
            className="!absolute left-0"
          >
            <ChevronLeftIcon />
          </IconButton>

          <div className="w-full text-center">
            <p className="text-xl max-3xl:text-lg text-[#1F2937] font-semibold">
              {currentMonthByText} {currentYear}
            </p>
          </div>

          <IconButton
            size="small"
            onClick={handleNextMonth}
            className="!absolute right-0"
          >
            <ChevronRightIcon />
          </IconButton>
        </div>
        <div className="">
          <div className="grid grid-cols-7 text-center">
            {weekDays.map((day) => (
              <div className="weekDays" key={day}>
                <p className="uppercase font-semibold text-[#49494B]">{day}</p>
              </div>
            ))}

          </div>

          <div className="grid grid-cols-7 text-center border-t border-l border-[#DBE8FF]">
            {prevMonthDays.map((day, index) => {
              const isCurrentDay = isToday(day);
              return (
                <div
                  className={clsx("day prev-month border-r border-b border-[#DBE8FF] cursor-default", {
                    "current-day": isCurrentDay,
                  })}
                  style={
                    isCurrentDay
                      ? {
                          backgroundColor: "#F5F8FF",
                  
                        }
                      : undefined
                  }
                  key={-index - 1}
                >
                  <div className="day-number">{format(day, "d")}</div>
                  <div className="event"></div>
                </div>
              );
            })}
            {daysInMonth.map((day, index) => {
              const eventsForDay = getEventsForDay(day);
              const showNoLeaves =
                eventsForDay.length === 0 ||
                eventsForDay.some(
                  (event) =>
                    event.numOfUser === 0 &&
                    !event.isHolidayNormal &&
                    !event.isHolidayRecurring
                );

              const isCurrentDay = isToday(day);
              return (
                <div
                  className={clsx(
                    "day border-r border-b border-[#DBE8FF] cursor-default",
                    {
                      "current-day": isCurrentDay,
                    }
                  )}
                  style={
                    isCurrentDay
                      ? {
                          backgroundColor: "#F5F8FF",
                  
                        }
                      : undefined
                  }
                >
                  <div className="day-number">{format(day, "d")}</div>

                  <MoreActionClickedTooltip title={getTooltipTitle(day)}>
                    {showNoLeaves && (
                      <MoreActionTooltip>
                        <div className="border border-[#E5E7EB] bg-[#FFFFFF] rounded-md min-h-[34px] flex items-center justify-center sm:min-h-[30px]">
                          <p
                            style={{ fontSize: "12px" }}
                            className="text-[#E5E7EB] sm:text-xs"
                          >
                            {t("common.content-122")}
                          </p>
                        </div>
                      </MoreActionTooltip>
                    )}
                    {eventsForDay.length !== 0 &&
                      eventsForDay.map((event, index) => (
                        <MoreActionTooltip
                          holidayDescription={event.description}
                          key={index}
                          leavesNumber={event.numOfUser}
                          date={event.date}
                        >
                          {!showNoLeaves && (
                            <div
                              className="rounded-md min-h-[22px] h-[22px] w-full flex items-center justify-center mt-1 px-1 gap-1 border"
                              style={
                                event.isHolidayRecurring === true
                                  ? {
                                      backgroundColor: "#F0FDF4",
                                      borderColor: "#058900",
                                    }
                                  : event.isHolidayNormal === true
                                  ? {
                                      backgroundColor: "#F3ECFD",
                                      borderColor: "#853EEF",
                                    }
                                  : {
                                      backgroundColor: "#FFFFFF",
                                      borderColor: "#E5E7EB",
                                    }
                              }
                            >
                              <div className="flex items-center gap-1">
                                {event.numOfUser > 0 &&
                                  !event.isHolidayNormal &&
                                  !event.isHolidayRecurring && (
                                    <div className="flex gap-1 items-center text-[#FF0066]">
                                      <UserRemoveIcon16pxSvg />
                                      <p>{event.numOfUser}</p>
                                    </div>
                                  )}

                                {event.isHolidayRecurring === true && (
                                  <ShiftRosterIcon20pxSvg color="#058900" />
                                )}
                                {event.isHolidayNormal === true && (
                                  <LeaveHolidayIcon20pxSvg color="#853EEF" />
                                )}
                              </div>
                            </div>
                          )}
                        </MoreActionTooltip>
                      ))}
                  </MoreActionClickedTooltip>
                </div>
              );
            })}
            {nextMonthDays.map((day, index) => {
              const isCurrentDay = isToday(day);
              return (
                <div
                  className={clsx("day next-month border-r border-b border-[#DBE8FF] cursor-default", {
                    "current-day": isCurrentDay,
                  })}
                  style={
                    isCurrentDay
                      ? {
                          backgroundColor: "#F5F8FF",
                  
                        }
                      : undefined
                  }
                  key={index + daysInMonth.length}
                >
                  <div className="day-number">{format(day, "d")}</div>
                  <div className="event"></div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex justify-center pt-4 max-3xl:pt-4 flex-wrap gap-3">
          {legends.map((item, idx) => (
            <div className="flex gap-1 items-center" key={idx}>
              {item.color}
              <p className="text-xs">{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MonthEventsCalendar;
